import React, { useRef } from 'react';
import { Box, Divider, TextField, Typography } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

const QuesitosPericiaisTrabalhistaForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Manifestação para Apresentação de Quesitos Periciais"
        customDescription="Peça para apresentar quesitos a serem respondidos pelo perito em processo trabalhista"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
          label="Escreva o nome do seu cliente:"
        />

        {/* 2. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Despacho que determina apresentação de quesitos e Processo na íntegra."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* 3. Fatos do Caso (Opcional) */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Conte mais sobre o caso trazendo a realidade dos fatos, se achar necessário: (Opcional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Descreva os pontos relevantes que deseja abordar nos quesitos periciais..."
            value={values.fatos_caso || ''}
            onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
            inputProps={{ maxLength: 6000 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {(values.fatos_caso || '').length} de 6000 caracteres permitidos.
          </Typography>
        </Box>

        {/* 4. Peça a Rebater */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Indique a peça do processo que deseja rebater ou responder: <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: Despacho que determina apresentação de quesitos"
            value={values.peca_rebater || 'Despacho que determina apresentação de quesitos'}
            onChange={(e) => handleFieldChange('peca_rebater', e.target.value)}
            helperText="Informe o despacho ou decisão que determinou a apresentação dos quesitos"
          />
        </Box>

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os pontos que deseja abordar nos quesitos periciais."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="Formule quesitos claros e objetivos que permitam ao perito esclarecer os pontos controvertidos. Foque nas questões técnicas relevantes para a tese do seu cliente." />
    </Box>
  );
};

export default QuesitosPericiaisTrabalhistaForm;

